import { useRef, useCallback, useEffect, useState } from 'react';
import { getIceServers } from '../lib/iceServers';

export const useWebRTC = ({
  userId,
  callId,
  otherUserId,
  isVideoCall,
  isCaller,
  onRemoteStream,
  onConnectionStateChange,
  onError
}) => {
  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const remoteDescSetRef = useRef(false);
  const peerReadyRef = useRef(false);
  const offerCreatedRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3;
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('new');

  const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

  // Handle incoming ICE candidate (buffer if remote desc not yet set)
  const handleIceCandidate = useCallback(async (candidate) => {
    try {
      const pc = peerConnectionRef.current;
      if (!pc) return;
      if (!remoteDescSetRef.current) {
        pendingCandidatesRef.current.push(candidate);
        return;
      }
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      console.error('Failed to add ICE candidate:', error);
    }
  }, []);

  // Drain buffered candidates after remote description is set
  const drainPendingCandidates = useCallback(async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return;
    const pending = pendingCandidatesRef.current.splice(0);
    for (const c of pending) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(c));
      } catch (e) {
        console.warn('Failed to apply buffered ICE candidate:', e);
      }
    }
  }, []);

  // Send offer (caller side) — only called after peer is ready
  const createAndSendOffer = useCallback(async () => {
    const pc = peerConnectionRef.current;
    if (!pc || offerCreatedRef.current) return;
    offerCreatedRef.current = true;
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'webrtc_offer',
          call_id: callId,
          to_user_id: otherUserId,
          offer: offer
        }));
      }
    } catch (e) {
      console.error('Failed to create offer:', e);
      onError?.('Could not start the call');
    }
  }, [callId, otherUserId, onError]);

  // Handle incoming offer (callee side)
  const handleOffer = useCallback(async (offer) => {
    try {
      const pc = peerConnectionRef.current;
      if (!pc) return;

      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      remoteDescSetRef.current = true;
      await drainPendingCandidates();

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'webrtc_answer',
          call_id: callId,
          to_user_id: otherUserId,
          answer: answer
        }));
      }
    } catch (error) {
      console.error('Failed to handle offer:', error);
    }
  }, [callId, otherUserId, drainPendingCandidates]);

  // Handle incoming answer (caller side)
  const handleAnswer = useCallback(async (answer) => {
    try {
      const pc = peerConnectionRef.current;
      if (!pc) return;
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
      remoteDescSetRef.current = true;
      await drainPendingCandidates();
    } catch (error) {
      console.error('Failed to handle answer:', error);
    }
  }, [drainPendingCandidates]);

  // Initialize WebSocket
  const initWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }

    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/${userId}`);

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        // If this side is the callee, announce ready as soon as PC + stream are set up
        // (See answerCall: it triggers sendPeerReady after stream is ready)
        if (peerReadyRef.current && !isCaller) {
          ws.send(JSON.stringify({
            type: 'peer_ready',
            call_id: callId,
            to_user_id: otherUserId
          }));
        }
      };

      ws.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'webrtc_offer' && message.data?.call_id === callId) {
            await handleOffer(message.data.offer);
          } else if (message.type === 'webrtc_answer' && message.data?.call_id === callId) {
            await handleAnswer(message.data.answer);
          } else if (message.type === 'webrtc_ice_candidate' && message.data?.call_id === callId) {
            await handleIceCandidate(message.data.candidate);
          } else if (message.type === 'peer_ready' && message.data?.call_id === callId) {
            // Caller: callee is ready → send the offer now
            if (isCaller) {
              await createAndSendOffer();
            }
          } else if (message.type === 'call_hangup' && message.data?.call_id === callId) {
            cleanup();
          } else if (message.type === 'call_answered' && message.data?.call_id === callId) {
            // Fallback: if caller hasn't received peer_ready (callee on older client), still try
            if (isCaller) {
              setTimeout(() => {
                if (!offerCreatedRef.current) createAndSendOffer();
              }, 500);
            }
          }
        } catch (e) {
          console.error('Error processing WebSocket message:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          setTimeout(() => initWebSocket(), 1500 * reconnectAttemptsRef.current);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      onError?.('Could not connect. Please check your internet.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, callId, otherUserId, isCaller, WS_URL, handleOffer, handleAnswer, handleIceCandidate, createAndSendOffer]);

  // Create peer connection
  const createPeerConnection = useCallback(async () => {
    if (peerConnectionRef.current) {
      try { peerConnectionRef.current.close(); } catch {}
      peerConnectionRef.current = null;
    }

    const iceServers = await getIceServers();
    const pc = new RTCPeerConnection({
      iceServers,
      iceCandidatePoolSize: 10,
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
    });

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'webrtc_ice_candidate',
          call_id: callId,
          to_user_id: otherUserId,
          candidate: event.candidate
        }));
      }
    };

    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      if (state === 'failed') {
        try { pc.restartIce(); } catch {}
      }
    };

    pc.ontrack = (event) => {
      if (event.streams[0]) onRemoteStream?.(event.streams[0]);
    };

    pc.onconnectionstatechange = () => {
      setConnectionState(pc.connectionState);
      onConnectionStateChange?.(pc.connectionState);
      if (pc.connectionState === 'failed') {
        onError?.('Connection failed. Please try again.');
      }
    };

    peerConnectionRef.current = pc;
    return pc;
  }, [callId, otherUserId, onRemoteStream, onConnectionStateChange, onError]);

  // Get local media stream with progressive fallback
  const getLocalStream = useCallback(async () => {
    const constraintOptions = isVideoCall ? [
      { video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true },
      { video: { width: { ideal: 640 }, height: { ideal: 480 } }, audio: true },
      { video: true, audio: true },
    ] : [
      { video: false, audio: true }
    ];

    let stream = null;
    let lastError = null;
    for (const constraints of constraintOptions) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (stream) break;
      } catch (e) {
        lastError = e;
      }
    }
    if (!stream) {
      const err = lastError || new Error('Failed to get media stream');
      let errorMsg = 'Could not access camera/microphone';
      if (err.name === 'NotAllowedError') errorMsg = 'Please grant camera and microphone permission';
      else if (err.name === 'NotFoundError') errorMsg = 'No camera or microphone found';
      else if (err.name === 'NotReadableError') errorMsg = 'Camera/microphone is already in use';
      onError?.(errorMsg);
      throw err;
    }
    localStreamRef.current = stream;
    return stream;
  }, [isVideoCall, onError]);

  // Wait for WebSocket OPEN
  const waitForWsOpen = useCallback(async () => {
    return new Promise((resolve) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return resolve();
      const ws = wsRef.current;
      if (!ws) return resolve();
      const onOpen = () => { ws.removeEventListener('open', onOpen); resolve(); };
      ws.addEventListener('open', onOpen);
      setTimeout(resolve, 5000); // safety timeout
    });
  }, []);

  // Start call as caller — sets up PC/stream, waits for peer_ready signal, then sends offer
  const startCall = useCallback(async () => {
    initWebSocket();
    const pc = await createPeerConnection();
    const stream = await getLocalStream();
    stream.getTracks().forEach(track => pc.addTrack(track, stream));
    await waitForWsOpen();
    // Offer will be created/sent when callee sends 'peer_ready'
    return stream;
  }, [initWebSocket, createPeerConnection, getLocalStream, waitForWsOpen]);

  // Answer call as callee — sets up PC/stream, signals 'peer_ready' to caller
  const answerCall = useCallback(async () => {
    initWebSocket();
    const pc = await createPeerConnection();
    const stream = await getLocalStream();
    stream.getTracks().forEach(track => pc.addTrack(track, stream));
    await waitForWsOpen();
    peerReadyRef.current = true;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'peer_ready',
        call_id: callId,
        to_user_id: otherUserId
      }));
    }
    return stream;
  }, [initWebSocket, createPeerConnection, getLocalStream, waitForWsOpen, callId, otherUserId]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        return !audioTrack.enabled;
      }
    }
    return false;
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    const stream = localStreamRef.current;
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        return !videoTrack.enabled;
      }
    }
    return false;
  }, []);

  // Cleanup
  const cleanup = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      try { peerConnectionRef.current.close(); } catch {}
      peerConnectionRef.current = null;
    }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
      wsRef.current = null;
    }
    pendingCandidatesRef.current = [];
    remoteDescSetRef.current = false;
    peerReadyRef.current = false;
    offerCreatedRef.current = false;
    setIsConnected(false);
    setConnectionState('closed');
  }, []);

  // End call
  const endCall = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'call_hangup',
        call_id: callId,
        to_user_id: otherUserId
      }));
    }
    cleanup();
  }, [callId, otherUserId, cleanup]);

  useEffect(() => {
    return () => { cleanup(); };
  }, [cleanup]);

  return {
    localStream: localStreamRef.current,
    isConnected,
    connectionState,
    startCall,
    answerCall,
    endCall,
    toggleMute,
    toggleVideo,
    cleanup
  };
};

export default useWebRTC;
